import { useEffect, useState, type FormEvent } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import type { Puzzle } from '../api/types';

interface Props {
  puzzle: Puzzle;
  onSaved(): void;
}

/**
 * Inline-Anzeige + Bearbeitung der Lösung. Wenn keine Lösung gesetzt
 * ist, sieht man direkt das Eingabefeld. Wenn schon eine Lösung
 * existiert, sieht man sie + einen kleinen Bearbeiten-Link.
 */
export function SolutionInline({ puzzle, onSaved }: Props) {
  const [editing, setEditing] = useState(puzzle.solution_year == null);
  const [value, setValue] = useState(
    puzzle.solution_year != null ? String(puzzle.solution_year) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wenn das Puzzle wechselt (Tag-Navigation), Form-State neu syncen.
  useEffect(() => {
    setValue(puzzle.solution_year != null ? String(puzzle.solution_year) : '');
    setEditing(puzzle.solution_year == null);
    setError(null);
  }, [puzzle.id, puzzle.solution_year]);

  if (!editing && puzzle.solution_year != null) {
    return (
      <p className="solution-display">
        🔎 Lösung: <strong>{puzzle.solution_year}</strong>
        <button
          type="button"
          className="solution-display__edit"
          onClick={() => setEditing(true)}
        >
          bearbeiten
        </button>
      </p>
    );
  }

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    const parsed = Number.parseInt(value.trim(), 10);
    if (!Number.isFinite(parsed)) {
      setError('Bitte eine Jahreszahl eintragen');
      return;
    }
    setBusy(true);
    try {
      await api.post('/puzzle', {
        puzzle_number: puzzle.puzzle_number,
        puzzle_date: puzzle.puzzle_date.slice(0, 10),
        solution_year: parsed,
      });
      onSaved();
      setEditing(false);
    } catch (err) {
      const text = axios.isAxiosError(err) && err.response?.data?.error
        ? String(err.response.data.error)
        : 'Speichern fehlgeschlagen';
      setError(text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="solution-inline" onSubmit={submit} data-no-swipe>
      <label className="field">
        <span className="field__label">
          {puzzle.solution_year == null ? 'Lösung nachtragen' : 'Lösung bearbeiten'}
        </span>
        <div className="solution-inline__row">
          <input
            className="field__input"
            type="number"
            inputMode="numeric"
            placeholder="z.B. 1972"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" className="button button--primary" disabled={busy || !value.trim()}>
            {busy ? '…' : 'Speichern'}
          </button>
          {puzzle.solution_year != null && (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                setValue(String(puzzle.solution_year));
                setEditing(false);
                setError(null);
              }}
              disabled={busy}
            >
              Abbrechen
            </button>
          )}
        </div>
      </label>
      {error && <p className="entry-form__error" role="alert">{error}</p>}
    </form>
  );
}
